import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CookLog,
  Difficulty,
  GetRecipeResponse as RecipeDetailData,
  Ingredient,
  IngredientInput,
  Scaling,
  Step,
  StepIngredient,
  StepIngredientInput,
  StepInput,
  Unit,
} from '@mlc/shared';
import { deplacerLigne, deplacerSection, joinQuantityUnit, ouvreUneSection, sectionDe, sectionsDe, transformerEnTitre, transformerTitreEnLigne, RECIPE_COURSES, formatCourse, type RecipeCourse, type StepOrderInput } from '@mlc/shared';
import { CANONICAL_UNITS, SCALING_MODES } from '@mlc/shared';
import { formatDureeSecondes, parseDureeSaisie } from '@mlc/shared';
// Réutilisation directe du moteur d'adaptation serveur (§3.1) pour le
// sélecteur de portions (US-07) — même principe que parseIngredientLine pour
// US-01 : fonction pure, sans dépendance Node, exécutée dans le navigateur.
// Rien n'est jamais envoyé au serveur : l'adaptation ne modifie pas la
// recette enregistrée (§3.1 : « ne modifie pas la recette enregistrée »).
import { adaptRecipeQuantities } from '@mlc/server/src/ingredients/adaptQuantities';
import type { AdaptedQuantity } from '@mlc/server/src/ingredients/adaptQuantities';
import { OfflineQueuedError, archiveRecipe, getRecipe, listCuisines, recipePdfUrl, reorderSteps, restoreRecipe, updateIngredients, updateRecipe, updateRecipeTags, updateStepIngredients, updateSteps, uploadPhoto } from './api';
import { readStoredTargetServings, writeStoredTargetServings } from './servingsStorage';
import { CookLogForm } from './CookLogForm';
import { TagsEditor } from './TagsEditor';
import { useUrl } from './router';
import { Badge, Banner, Button, Checkbox, CollapsiblePanel, FOCUS_RING, Icon, LinkButton, Loading, PageHeader, Select, cardClasses, fieldClasses } from './ui';

export interface RecipeDetailProps {
  recipeId: string;
  /**
   * Retour à la fiche.
   *
   * `cooking` et `onCookingChange` ont disparu en US-58 : `App.tsx` passait
   * `cooking={false}` en dur à cet écran depuis US-13, et le `<CookMode>` qu'il
   * montait n'avait donc plus aucun chemin d'exécution. Du code mort qui
   * ressemblait à une fonctionnalité.
   */
  onBack: () => void;
}

// Style de champ partagé (B1, refonte UX/UI passe 3) : densité « compact »,
// réservée aux grilles d'édition permanente (IngredientsEditor, StepsEditor
// ci-dessous) — voir ui/Field.tsx. `EditableField` (édition sur place, juste
// en dessous) a son propre style : voir `editableFieldClasses`.
const inputClass = fieldClasses('compact');
const labelClass = 'flex flex-col gap-1 text-sm';

function toNullableNumber(value: string): number | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : Number(trimmed);
}

function toNullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** Évite la dérive en virgule flottante d'incréments répétés de 0,5 (audit UX/UI passe 2, I2). */
function roundToHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

/**
 * Style de lecture par défaut, révélé comme champ au survol/focus (audit
 * UX/UI passe 2, B5 : « un style “lecture” proche du texte courant, qui se
 * révèle comme champ éditable au survol/focus »). Bordure et fond
 * transparents au repos — seul un léger `padding` négatif en `margin`
 * compense le décalage du texte pour qu'il reste aligné avec le texte
 * courant environnant tant que le champ n'est pas actif.
 */
// `min-h-touch-min` (US-25) : discret ne veut pas dire minuscule. La zone de
// frappe reste au plancher de 44 px du §4 — c'était 26 px, soit une cible
// qu'on rate au doigt une fois sur trois.
//
// **Le champ se voit désormais au repos** (refonte éditoriale). La règle B5
// de la passe 4 — « un style lecture proche du texte courant, qui se révèle
// comme champ au survol/focus » — a été écrite pour la *fiche en lecture*,
// où un champ visible au milieu d'une recette qu'on parcourt serait du bruit.
// Elle a été appliquée telle quelle à `EditableField`, qui n'est monté que
// sur `/recette/:id/modifier` — l'écran dont c'est **l'unique fonction**.
//
// Le résultat, sur une capture : « 6 », « pers. », « française », « 480 »
// posés en texte nu au milieu de listes déroulantes bordées et de cases à
// cocher. On ne voyait pas ce qui était modifiable, et rien ne distinguait
// une valeur d'une étiquette. C'est aussi ce que WCAG 1.4.11 refuse : un
// contrôle a droit à 3:1 contre ce qui l'entoure.
//
// Le compromis retenu garde l'intention de B5 sans son effet : un filet fin
// au repos — assez pour dire « ceci se modifie », pas assez pour transformer
// la page en formulaire — qui se renforce au survol et prend l'accent au
// focus. Trois états au lieu de deux.
//
// Le survol pose un fond en plus du filet : sur un écran où trente champs se
// suivent, c'est le fond, pas la bordure, qui dit lequel est sous le curseur.
const editableFieldClasses =
  `w-full min-h-touch-min rounded-control border border-border bg-transparent px-1.5 py-0.5 -mx-1.5 -my-0.5 text-foreground transition-colors duration-(--duration-quick) ease-entrance hover:border-border-interactive hover:bg-surface ${FOCUS_RING} focus:border-primary focus:bg-surface`;

/** Résultat d'`onCommit` : `void` pour un enregistrement local, une promesse résolue en succès/échec pour un enregistrement réseau (indicateur transitoire ci-dessous). */
type CommitResult = void | Promise<boolean>;

function isPromise(value: CommitResult): value is Promise<boolean> {
  return typeof value === 'object' && value !== null && typeof value.then === 'function';
}

/**
 * Champ texte ou nombre édité sur place (US-06) : enregistré au blur, jamais
 * à chaque frappe. `required` annule et restaure la valeur précédente plutôt
 * que d'enregistrer une chaîne vide (utilisé pour le titre, seul champ
 * obligatoire).
 *
 * Distinction lecture/formulaire (B5, refonte UX/UI passe 4) : au repos, le
 * champ se lit comme du texte courant (pas de bordure visible) ; il ne se
 * révèle comme champ éditable qu'au survol ou au focus. Un indicateur
 * transitoire (« Enregistré », coche discrète) apparaît en surimpression —
 * jamais dans le flux — pour ne jamais décaler le contenu autour (§10 de la
 * mission : « un feedback discret … qui ne déplace pas la mise en page »).
 */
function EditableField({
  value,
  onCommit,
  type = 'text',
  multiline = false,
  required = false,
  placeholder,
  className = '',
  list,
}: {
  value: string;
  onCommit: (raw: string) => CommitResult;
  type?: 'text' | 'number';
  multiline?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  /** Identifiant d'un `<datalist>` de suggestions (US-55). Le champ reste libre. */
  list?: string;
}): React.JSX.Element {
  const [local, setLocal] = useState(value);
  // Resynchronise l'état local quand `value` change depuis l'extérieur (ex. la
  // fiche complète est rechargée après l'enregistrement d'un autre champ).
  // Ajustement pendant le rendu plutôt que dans un effet : évite un rendu
  // supplémentaire (https://react.dev/learn/you-might-not-need-an-effect).
  const [syncedValue, setSyncedValue] = useState(value);
  if (value !== syncedValue) {
    setSyncedValue(value);
    setLocal(value);
  }

  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  function commit(): void {
    if (required && local.trim().length === 0) {
      setLocal(value);
      return;
    }
    if (local === value) return;
    const result = onCommit(local);
    if (!isPromise(result)) return;
    setStatus('saving');
    void result.then((ok) => {
      if (!mountedRef.current) return;
      if (!ok) {
        setStatus('idle');
        return;
      }
      setStatus('saved');
      window.setTimeout(() => {
        if (mountedRef.current) setStatus((current) => (current === 'saved' ? 'idle' : current));
      }, 1600);
    });
  }

  const fieldClass = `${editableFieldClasses} ${className}`.trim();

  return (
    <span className="relative inline-flex w-full items-center">
      {multiline ? (
        <textarea
          className={fieldClass}
          value={local}
          placeholder={placeholder}
          rows={3}
          onChange={(e) => { setLocal(e.target.value); }}
          onBlur={commit}
        />
      ) : (
        <input
          className={fieldClass}
          type={type}
          inputMode={type === 'number' ? 'decimal' : undefined}
          list={list}
          value={local}
          placeholder={placeholder}
          onChange={(e) => { setLocal(e.target.value); }}
          onBlur={commit}
        />
      )}
      {/* Indicateur transitoire (B5) : en surimpression (`absolute`), jamais
          dans le flux — il ne doit jamais décaler le texte voisin. */}
      {status === 'saved' && (
        <span
          aria-hidden="true"
          className="animate-fade-in pointer-events-none absolute -right-1 top-1/2 flex -translate-y-1/2 translate-x-full items-center gap-1 pl-2 text-xs font-medium text-success"
        >
          <Icon name="coche" /> Enregistré
        </span>
      )}
      <span role="status" className="sr-only">
        {status === 'saving' ? 'Enregistrement…' : status === 'saved' ? 'Enregistré' : ''}
      </span>
    </span>
  );
}

function LastCookLog({ latestCookLog }: { latestCookLog: RecipeDetailData['latest_cook_log'] }): React.JSX.Element {
  if (latestCookLog === null) {
    return (
      <p
        data-testid="last-cook-log"
        className={cardClasses({ padded: false, className: 'px-3 py-2 text-sm text-foreground-muted' })}
      >
        Jamais cuisinée pour l’instant.
      </p>
    );
  }

  const date = new Date(latestCookLog.cooked_at).toLocaleDateString('fr-FR');
  return (
    <div data-testid="last-cook-log" className={cardClasses({ padded: false, className: 'px-3 py-2 text-sm text-foreground' })}>
      <p className="font-medium">
        Dernière fois — <span className="text-data">{date}</span>
        {latestCookLog.rating !== null ? <span className="text-data"> · {latestCookLog.rating}/5</span> : ''}
      </p>
      {latestCookLog.next_time !== null && <p className="mt-1">{latestCookLog.next_time}</p>}
    </div>
  );
}

/**
 * Historique complet des réalisations (US-11 : « consultable, trié du plus
 * récent au plus ancien » — déjà l'ordre renvoyé par l'API, cf. DECISIONS.md
 * « US-10/US-11 »). Repliée par défaut, un bouton suffit à la déplier : la
 * fiche recette affiche déjà la dernière note en évidence (`LastCookLog`
 * ci-dessus), pas besoin d'imposer tout l'historique au défilement de chaque
 * visite — cohérent avec « consultable » plutôt que « visible en permanence »
 * (contrairement à `next_time`, § AC US-11). Repli par état contrôlé plutôt
 * que `<details>`/`<summary>` : évite de dépendre du comportement natif de
 * bascule au clic, dont le support en environnement de test (jsdom) est
 * moins garanti qu'un simple état React.
 */
function CookLogHistory({ cookLogs }: { cookLogs: CookLog[] }): React.JSX.Element | null {
  if (cookLogs.length === 0) return null;

  return (
    <CollapsiblePanel
      label={`Historique des réalisations (${cookLogs.length})`}
      sectionLabel="Historique des réalisations"
    >
      <ul data-testid="cook-log-history" className="mt-3 flex flex-col gap-3">
        {cookLogs.map((log) => (
          <li
            key={log.id}
            className="flex flex-col gap-1 border-t border-border pt-3 first:border-t-0 first:pt-0"
          >
            <p className="font-medium">
              <span className="text-data">{new Date(log.cooked_at).toLocaleDateString('fr-FR')}</span>
              {log.rating !== null ? <span className="text-data"> · {log.rating}/5</span> : ''}
              {log.actual_minutes !== null ? <span className="text-data"> · {log.actual_minutes} min</span> : ''}
            </p>
            {log.what_happened !== null && <p>{log.what_happened}</p>}
            {log.next_time !== null && <p className="text-foreground-secondary">À retenir : {log.next_time}</p>}
            {log.photo_path !== null && (
              // `alt=""` décorative (même raisonnement que PhotoField, US-06) : retire
              // l'image du rôle accessible « img », `data-testid` la cible en test.
              <img
                data-testid={`cook-log-photo-${log.id}`}
                src={`/api/photos/${log.photo_path}`}
                alt=""
                className="mt-1 h-24 w-24 rounded-control object-cover"
              />
            )}
          </li>
        ))}
      </ul>
    </CollapsiblePanel>
  );
}
function PhotoField({
  imagePath,
  uploading,
  error,
  onChange,
}: {
  imagePath: string | null;
  uploading: boolean;
  error: string | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2 sm:w-56">
      <label
        htmlFor="recipe-photo-input"
        className="flex aspect-[4/3] w-full cursor-pointer items-center justify-center overflow-hidden rounded-card border border-dashed border-border-strong bg-surface-raised text-sm"
      >
        {imagePath !== null ? (
          // Photo principale (US-06) : servie par le serveur sous /photos (§2 : fichiers, jamais en base).
          // `alt=""` (décorative, le titre porte déjà l'information) retire l'image du rôle accessible
          // « img » : `data-testid` est le seul moyen fiable de la cibler en test.
          <img
            data-testid="recipe-photo"
            src={`/api/photos/${imagePath}`}
            alt=""
            // Apparition en fondu (I5, refonte UX/UI passe 4) : cet élément
            // n'est monté que lorsqu'une photo vient d'être envoyée ou
            // chargée, jamais réutilisé pour une autre — l'animation ne
            // rejoue donc jamais à tort au fil des re-rendus.
            className="h-full w-full animate-fade-in object-cover"
          />
        ) : (
          <span className="p-4 text-center text-foreground-muted">{uploading ? 'Envoi…' : 'Ajouter une photo'}</span>
        )}
      </label>
      <input
        id="recipe-photo-input"
        aria-label="Photo principale"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={onChange}
        disabled={uploading}
      />
      {error !== null && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface IngredientRow extends IngredientInput {
  key: string;
}

function toIngredientRows(ingredients: Ingredient[]): IngredientRow[] {
  return ingredients.map((ingredient) => ({
    key: ingredient.id,
    id: ingredient.id,
    group_label: ingredient.group_label,
    quantity: ingredient.quantity,
    unit: ingredient.unit,
    name: ingredient.name,
    note: ingredient.note,
    is_optional: ingredient.is_optional,
    scaling: ingredient.scaling,
  }));
}

function toIngredientInputs(rows: IngredientRow[]): IngredientInput[] {
  return rows.map(({ key: _key, ...input }) => input);
}

/**
 * Regroupe des lignes par `group_label`, dans l'ordre de première apparition
 * (§6 : ingrédients et étapes « groupés »). Un groupe sans étiquette (`null`)
 * s'affiche sans en-tête plutôt que sous un « Sans groupe » qui n'ajoute rien.
 */
function groupByLabel<T extends { group_label: string | null }>(rows: T[]): { label: string | null; items: T[] }[] {
  const groups: { label: string | null; items: T[] }[] = [];
  for (const row of rows) {
    const group = groups.find((candidate) => candidate.label === row.group_label);
    if (group) {
      group.items.push(row);
    } else {
      groups.push({ label: row.group_label, items: [row] });
    }
  }
  return groups;
}

interface AdaptedRow {
  key: string;
  group_label: string | null;
  ingredient: Ingredient;
  adapted: AdaptedQuantity;
}

/**
 * Liste en lecture seule des ingrédients adaptés (US-07), regroupée comme la
 * liste éditable (US-06) pour rester repérable. Ne remplace pas
 * `IngredientsEditor` : celui-ci reste la vue d'édition des quantités de
 * référence, celle-ci n'est qu'un calcul d'affichage, jamais enregistré.
 */
function AdaptedIngredientsList({
  ingredients,
  adapted,
}: {
  ingredients: Ingredient[];
  adapted: AdaptedQuantity[];
}): React.JSX.Element {
  const rows: AdaptedRow[] = ingredients.map((ingredient, index) => ({
    key: ingredient.id,
    group_label: ingredient.group_label,
    ingredient,
    adapted: adapted[index] ?? { quantity: null, unit: ingredient.unit, display: null, note: null },
  }));
  const groups = groupByLabel(rows);

  return (
    <ul
      aria-label="Ingrédients adaptés"
      data-testid="adapted-ingredients"
      className="flex flex-col gap-3 text-sm"
    >
      {groups.map((group) => (
        <li key={group.label ?? ' sans-groupe'} className="flex flex-col gap-1">
          {group.label !== null && <h3 className="text-xs font-semibold text-foreground-muted">{group.label}</h3>}
          <ul className="flex flex-col gap-1">
            {group.items.map((row) => (
              <li key={row.key} data-testid={`adapted-ingredient-${row.ingredient.id}`} className="text-foreground">
                {row.adapted.display !== null && (
                  <span className="text-data">
                    {joinQuantityUnit(row.adapted.display, row.ingredient.unit, row.adapted.quantity)}
                    {' '}
                  </span>
                )}
                {row.ingredient.name}
                {row.ingredient.note !== null && <span className="text-foreground-muted"> ({row.ingredient.note})</span>}
                {row.adapted.note !== null && <span className="text-foreground-muted"> — {row.adapted.note}</span>}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/**
 * Adaptation des portions (US-07). Distinct du champ « Portions » du bloc
 * ci-dessus, qui édite `recipe.servings` — la référence permanente de la
 * recette (US-06). Ici, rien n'est jamais enregistré côté serveur : le choix
 * n'est mémorisé que pour la session de cuisine en cours (sessionStorage),
 * afin de rester disponible tel quel quand US-08 branchera le mode cuisine
 * sur la même clé de mémorisation.
 */
function ServingsAdapter({
  recipeId,
  referenceServings,
  ingredients,
}: {
  recipeId: string;
  referenceServings: number | null;
  ingredients: Ingredient[];
}): React.JSX.Element {
  const [target, setTarget] = useState<number | null>(
    () => readStoredTargetServings(recipeId) ?? referenceServings,
  );

  // Recette changée : on repart de la valeur mémorisée pour CETTE recette (ou
  // à défaut sa référence), pas de celle de la recette précédemment affichée.
  const [syncedRecipeId, setSyncedRecipeId] = useState(recipeId);
  if (recipeId !== syncedRecipeId) {
    setSyncedRecipeId(recipeId);
    setTarget(readStoredTargetServings(recipeId) ?? referenceServings);
  }

  function handleChange(raw: string): void {
    const value = toNullableNumber(raw);
    const usable = value !== null && Number.isFinite(value) && value > 0 ? value : null;
    setTarget(usable);
    writeStoredTargetServings(recipeId, usable);
  }

  const result = useMemo(() => {
    if (referenceServings === null || target === null) return null;
    return adaptRecipeQuantities({
      referenceServings,
      targetServings: target,
      ingredients: ingredients.map((ingredient) => ({
        quantity: ingredient.quantity,
        unit: ingredient.unit,
        scaling: ingredient.scaling,
        name: ingredient.name,
      })),
      steps: [],
    });
  }, [referenceServings, target, ingredients]);

  return (
    <section
      aria-label="Adapter les portions"
      className={cardClasses({ className: 'flex flex-col gap-3' })}
    >
      <h2 className="text-heading-2">Adapter les portions</h2>

      {referenceServings === null ? (
        <p className="text-sm text-foreground-muted">
          Renseignez le nombre de portions de la recette (ci-dessus) pour activer l’adaptation des quantités.
        </p>
      ) : (
        <>
          <div className={labelClass}>
            <span>Portions souhaitées</span>
            {/* Stepper tactile (audit UX/UI passe 2, I2 : « un clavier
                numérique … est plus lourd qu'un stepper à deux boutons » sur
                écran tactile). Le champ reste saisissable directement au
                clavier, le stepper n'est qu'un raccourci. */}
            <span className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="shrink-0"
                aria-label="Diminuer les portions souhaitées"
                onClick={() => { handleChange(String(roundToHalf(Math.max(0.5, (target ?? referenceServings ?? 0) - 0.5)))); }}
                disabled={target !== null && target <= 0.5}
              >
                <Icon name="moins" />
              </Button>
              <input
                aria-label="Portions souhaitées"
                className={`${inputClass} text-data w-16 text-center`}
                type="number"
                inputMode="decimal"
                min="0.5"
                step="0.5"
                value={target ?? ''}
                onChange={(e) => { handleChange(e.target.value); }}
              />
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="shrink-0"
                aria-label="Augmenter les portions souhaitées"
                onClick={() => { handleChange(String(roundToHalf((target ?? referenceServings ?? 0) + 0.5))); }}
              >
                <Icon name="plus" />
              </Button>
            </span>
          </div>

          {target === null && (
            <p className="text-sm text-foreground-muted">Indiquez un nombre de portions valide pour voir les quantités adaptées.</p>
          )}

          {result !== null && result.warning && (
            <Banner tone="warning">
              Adaptation importante — les temps de cuisson et la taille du récipient sont probablement à revoir.
            </Banner>
          )}

          {result !== null && <AdaptedIngredientsList ingredients={ingredients} adapted={result.ingredients} />}
        </>
      )}
    </section>
  );
}

/**
 * Ingrédients édités sur place (US-06) : chaque ligne est corrigeable
 * directement, sans écran de saisie séparé. Remplacement intégral de la
 * liste à chaque enregistrement (pas de calcul de différence) — plus simple
 * et plus sûr pour une liste courte éditée par une seule personne ; pas de
 * réordonnancement par glisser-déposer dans cette story.
 */
/**
 * Libellés lisibles des unités canoniques dans le sélecteur (US-15, §3.5).
 * Les valeurs restent canoniques ; seuls les libellés changent.
 */
const UNIT_OPTION_LABEL: Record<Unit, string> = {
  g: 'g (grammes)',
  kg: 'kg (kilogrammes)',
  ml: 'ml (millilitres)',
  cl: 'cl (centilitres)',
  l: 'l (litres)',
  'c. à c.': 'c. à c. (cuillère à café)',
  'c. à s.': 'c. à s. (cuillère à soupe)',
  piece: 'pièce',
  pincee: 'pincée',
  botte: 'botte',
  gousse: 'gousse',
  brin: 'brin',
  tranche: 'tranche',
  pot: 'pot (mesure de maison)',
  sachet: 'sachet',
  verre: 'verre (mesure de maison)',
  cuillere: 'cuillère (mesure de maison)',
  bol: 'bol (mesure de maison)',
  noix: 'noix (mesure de maison)',
  cup: 'cup (240 ml)',
  oz: 'oz (28,35 g)',
  lb: 'lb (453,6 g)',
  tsp: 'tsp (cuillère à café US)',
  tbsp: 'tbsp (cuillère à soupe US)',
};

/**
 * Comportement de mise à l'échelle en français (§3.1). `linear`, `none` et
 * `sublinear` étaient proposés bruts, sans libellé, sur chaque ligne
 * d'ingrédient (audit externe, constat 09).
 */
const SCALING_LABEL: Record<Scaling, string> = {
  linear: 'Multiplier',
  none: 'Ne pas changer',
  sublinear: 'Augmenter un peu seulement',
};

function IngredientsEditor({
  ingredients,
  onSave,
}: {
  ingredients: Ingredient[];
  onSave: (rows: IngredientInput[]) => void;
}): React.JSX.Element {
  const [rows, setRows] = useState<IngredientRow[]>(() => toIngredientRows(ingredients));
  const [openOptions, setOpenOptions] = useState<ReadonlySet<string>>(() => new Set());
  const [syncedIngredients, setSyncedIngredients] = useState(ingredients);
  if (ingredients !== syncedIngredients) {
    setSyncedIngredients(ingredients);
    setRows(toIngredientRows(ingredients));
  }

  function toggleOptions(key: string): void {
    setOpenOptions((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function updateRow(key: string, patch: Partial<IngredientRow>): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function commitRows(): void {
    setRows((current) => {
      onSave(toIngredientInputs(current));
      return current;
    });
  }

  function addRow(): void {
    const key = crypto.randomUUID();
    const next: IngredientRow[] = [
      ...rows,
      { key, id: key, group_label: null, quantity: null, unit: null, name: '', note: null, is_optional: 0, scaling: 'linear' },
    ];
    setRows(next);
    onSave(toIngredientInputs(next));
  }

  function removeRow(key: string): void {
    const next = rows.filter((row) => row.key !== key);
    setRows(next);
    onSave(toIngredientInputs(next));
  }

  /**
   * Conversion ligne ↔ titre de section (US-41).
   *
   * La reconnaissance automatique est volontairement étroite : elle ne retient
   * que « Pour … » et « … : », et laisse passer les intitulés nus comme
   * « Poolish », qu'aucune règle ne distingue d'un vrai ingrédient. C'est ici
   * que l'utilisateur tranche, d'un geste, dans les deux sens.
   */
  function enTitre(key: string): void {
    const index = rows.findIndex((row) => row.key === key);
    if (index < 0) return;
    const next = transformerEnTitre(rows, index, (row) => row.name);
    setRows(next);
    onSave(toIngredientInputs(next));
  }

  function enIngredient(key: string): void {
    const index = rows.findIndex((row) => row.key === key);
    if (index < 0) return;
    const next = transformerTitreEnLigne(rows, index, (texte, groupe) => {
      const cle = crypto.randomUUID();
      // Le titre redevient une ligne à analyser : le parseur reprend la main
      // dessus comme sur n'importe quelle saisie (§3.2).
      const ligne: IngredientRow = { key: cle, id: null, group_label: groupe, quantity: null, unit: null, name: texte, note: null, is_optional: 0, scaling: 'linear' };
      return ligne;
    });
    setRows(next);
    onSave(toIngredientInputs(next));
  }

  const groups = groupByLabel(rows);

  return (
    <section aria-label="Ingrédients" className="flex flex-col gap-3">
      <h2 className="text-heading-2">Ingrédients</h2>
      <div data-testid="ingredient-editor" className="flex flex-col gap-4">
        {groups.map((group) => (
          <div key={group.label ?? ' sans-groupe'} className="flex flex-col gap-2">
            {/* Même traitement que sur la fiche en lecture : un titre de phase
                a le poids d'un titre de section, prolongé d'un filet qui
                marque la rupture. Il ne doit pas pouvoir se confondre avec une
                ligne de la liste (relecture de CookBook, 10 août 2026). */}
            {group.label !== null && (
              <h3 className="flex items-center gap-3">
                <span className="text-heading-2">{group.label}</span>
                <span aria-hidden="true" className="h-px flex-1 bg-border" />
              </h3>
            )}
            <ul className="flex flex-col gap-2">
              {group.items.map((row) => (
                <li
                  key={row.key}
                  className="flex flex-wrap items-center gap-2 rounded-control border border-border px-2 py-1"
                >
                  <input
                    aria-label="Quantité"
                    className={`${inputClass} text-data w-16`}
                    type="number"
                    inputMode="decimal"
                    value={row.quantity ?? ''}
                    onChange={(e) => { updateRow(row.key, { quantity: toNullableNumber(e.target.value) }); }}
                    onBlur={commitRows}
                  />
                  <Select
                    density="compact"
                    aria-label="Unité"
                    className="text-data"
                    value={row.unit ?? ''}
                    onChange={(e) => {
                      const unit = (e.target.value === '' ? null : e.target.value) as Unit | null;
                      updateRow(row.key, { unit });
                      commitRows();
                    }}
                  >
                    {/* Libellés lisibles, valeurs canoniques (US-15, §3.5) :
                        « pincée » à l'écran, `pincee` en base. */}
                    <option value="">sans unité</option>
                    {CANONICAL_UNITS.map((unit) => (
                      <option key={unit} value={unit}>{UNIT_OPTION_LABEL[unit]}</option>
                    ))}
                  </Select>
                  <input
                    aria-label="Nom de l’ingrédient"
                    className={`${inputClass} min-w-32 flex-1`}
                    value={row.name}
                    onChange={(e) => { updateRow(row.key, { name: e.target.value }); }}
                    onBlur={commitRows}
                  />
                  <input
                    aria-label="Note sur l’ingrédient"
                    className={`${inputClass} min-w-24 flex-1`}
                    placeholder="finement émincé…"
                    value={row.note ?? ''}
                    onChange={(e) => { updateRow(row.key, { note: e.target.value }); }}
                    onBlur={commitRows}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="compact"
                    aria-expanded={openOptions.has(row.key)}
                    aria-label={`Options de l’ingrédient ${row.name.length > 0 ? row.name : ''}`}
                    onClick={() => { toggleOptions(row.key); }}
                  >
                    Options
                  </Button>
                  {/* Bascule ligne ↔ titre (US-41). Le libellé dit ce qui va se
                      passer, pas l'état courant : « En faire un titre » sur une
                      ligne ordinaire, « Remettre en ingrédient » sur celle qui
                      ouvre une section. */}
                  {row.group_label !== null && ouvreUneSection(rows, rows.indexOf(row)) ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      onClick={() => { enIngredient(row.key); }}
                      aria-label={`Remettre le titre ${row.group_label} en ingrédient`}
                    >
                      Remettre en ingrédient
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      onClick={() => { enTitre(row.key); }}
                      disabled={row.name.trim().length === 0}
                      aria-label={`Faire de ${row.name.length > 0 ? row.name : 'cette ligne'} un titre de section`}
                    >
                      En faire un titre
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="compact"
                    className="ml-auto"
                    onClick={() => { removeRow(row.key); }}
                    aria-label={`Supprimer l’ingrédient ${row.name.length > 0 ? row.name : ''}`}
                  >
                    Supprimer
                  </Button>

                  {openOptions.has(row.key) && (
                    <div className="flex w-full flex-wrap items-center gap-3 border-t border-border pt-2 text-xs text-foreground">
                      <label className="flex items-center gap-1">
                        Groupe
                        <input
                          aria-label="Groupe de l’ingrédient"
                          className={`${inputClass} w-28`}
                          placeholder="Pour la pâte…"
                          value={row.group_label ?? ''}
                          onChange={(e) => { updateRow(row.key, { group_label: e.target.value }); }}
                          onBlur={commitRows}
                        />
                      </label>
                      <span className="flex min-h-touch items-center gap-2">
                        <Checkbox
                          size="compact"
                          label="Facultatif"
                          checked={row.is_optional === 1}
                          onChange={(next) => {
                            updateRow(row.key, { is_optional: next ? 1 : 0 });
                            commitRows();
                          }}
                        />
                        Facultatif
                      </span>
                      {/* `linear` / `none` / `sublinear` étaient proposés bruts,
                          sans libellé, sur chaque ligne (audit externe,
                          constat 09). Même valeur en base, phrase à l'écran. */}
                      <label className="flex items-center gap-1">
                        Quand on change les portions
                        <Select
                          density="compact"
                          aria-label="Mise à l’échelle"
                          value={row.scaling}
                          onChange={(e) => {
                            updateRow(row.key, { scaling: e.target.value as Scaling });
                            commitRows();
                          }}
                        >
                          {SCALING_MODES.map((mode) => (
                            <option key={mode} value={mode}>{SCALING_LABEL[mode]}</option>
                          ))}
                        </Select>
                      </label>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <Button type="button" variant="secondary" size="compact" className="w-fit" onClick={addRow}>
        + Ajouter un ingrédient
      </Button>
    </section>
  );
}

interface StepRow extends StepInput {
  key: string;
  /**
   * Origine des valeurs détectées (US-78), portée pour l'affichage seul : une
   * valeur `'auto'` garde le ton « à vérifier » tant qu'une main ne l'a pas
   * touchée. Le serveur décide seul de l'origine — ces champs ne repartent
   * jamais dans `StepInput`.
   */
  duration_source: Step['duration_source'];
  temperature_source: Step['temperature_source'];
  /**
   * La durée **telle qu'elle est écrite dans le champ** (US-98) — « 20 sec »,
   * « 1 h 30 », « 30 ». Elle ne part jamais au serveur : c'est
   * `parseDureeSaisie` qui, à la sortie du champ, en tire les secondes de
   * `duration_sec`. La garder ici est ce qui permet de taper « 1 h 30 » sans
   * que « 1 h 3 » soit lu au passage.
   */
  duree_texte: string;
}

function toStepRows(steps: Step[]): StepRow[] {
  return steps.map((step) => ({
    key: step.id,
    id: step.id,
    group_label: step.group_label,
    instruction: step.instruction,
    duration_sec: step.duration_sec,
    duree_texte: formatDureeSecondes(step.duration_sec) ?? '',
    temperature_c: step.temperature_c,
    duration_source: step.duration_source,
    temperature_source: step.temperature_source,
    is_passive: step.is_passive,
  }));
}

function toStepInputs(rows: StepRow[]): StepInput[] {
  return rows.map(
    ({ key: _key, duration_source: _ds, temperature_source: _ts, duree_texte: _dt, ...input }) => input,
  );
}

/** Étapes éditées sur place (US-06), même principe que les ingrédients ci-dessus. */
function StepsEditor({
  steps,
  ingredients,
  stepIngredients,
  onSave,
  onReorder,
  onSaveStepIngredients,
  ouvrirSurLesEtapes = false,
}: {
  steps: Step[];
  ingredients: Ingredient[];
  stepIngredients: StepIngredient[];
  onSave: (rows: StepInput[]) => void;
  /** L'ordre seul (US-77) : `position` et `group_label`, rien d'autre. */
  onReorder: (steps: StepOrderInput[]) => void;
  onSaveStepIngredients: (pairs: StepIngredientInput[]) => void;
  /** Arrivée depuis « Écrire les étapes » (US-74) : la section prend le focus. */
  ouvrirSurLesEtapes?: boolean;
}): React.JSX.Element {
  const linked = new Set(stepIngredients.map((pair) => `${pair.step_id} ${pair.ingredient_id}`));
  const persistedStepIds = new Set(steps.map((step) => step.id));

  function toggleIngredient(stepId: string, ingredientId: string): void {
    const key = `${stepId} ${ingredientId}`;
    onSaveStepIngredients(
      linked.has(key)
        ? stepIngredients.filter((pair) => !(pair.step_id === stepId && pair.ingredient_id === ingredientId))
        : [...stepIngredients, { step_id: stepId, ingredient_id: ingredientId }],
    );
  }

  const [rows, setRows] = useState<StepRow[]>(() => toStepRows(steps));
  const [syncedSteps, setSyncedSteps] = useState(steps);
  if (steps !== syncedSteps) {
    setSyncedSteps(steps);
    setRows(toStepRows(steps));
  }

  const [openOptions, setOpenOptions] = useState<ReadonlySet<string>>(() => new Set());

  function toggleOptions(key: string): void {
    setOpenOptions((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function updateRow(key: string, patch: Partial<StepRow>): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function commitRows(): void {
    setRows((current) => {
      onSave(toStepInputs(current));
      return current;
    });
  }

  function addRow(): void {
    const key = crypto.randomUUID();
    const next: StepRow[] = [
      ...rows,
      { key, id: key, group_label: null, instruction: '', duration_sec: null, duree_texte: '', temperature_c: null, duration_source: null, temperature_source: null, is_passive: 0 },
    ];
    setRows(next);
    onSave(toStepInputs(next));
  }

  function removeRow(key: string): void {
    const next = rows.filter((row) => row.key !== key);
    setRows(next);
    onSave(toStepInputs(next));
  }

  /**
   * Déplacement d'une étape (US-77).
   *
   * ## Un seul chemin d'écriture, et une exception nommée
   *
   * L'ordre part par `onReorder` — `PUT .../steps/ordre`, qui n'écrit que
   * `position` et `group_label`. `onSave` (`PUT .../steps`) efface et réinsère
   * toutes les étapes, ce qui fait recalculer l'appariement étape ↔ ingrédient
   * et perd les corrections faites à la main (US-08).
   *
   * L'exception : tant qu'une ligne n'a pas d'identifiant en base — juste après
   * « Remettre en étape », le temps d'un aller-retour —, il n'y a rien à
   * réordonner côté serveur. On retombe alors sur `onSave`, qui sait créer. Le
   * cas est bref et rare, mais le taire produirait un déplacement qui ne
   * s'enregistre pas.
   */
  const [annonce, setAnnonce] = useState('');

  function persisterLOrdre(suivantes: StepRow[]): void {
    setRows(suivantes);
    const enBase: StepOrderInput[] = [];
    for (const row of suivantes) {
      if (row.id == null || !persistedStepIds.has(row.id)) break;
      enBase.push({ id: row.id, group_label: row.group_label });
    }
    if (enBase.length === suivantes.length) {
      onReorder(enBase);
    } else {
      onSave(toStepInputs(suivantes));
    }
  }

  /** Monter ou descendre **une étape**. `sens` vaut -1 ou +1. */
  function deplacer(key: string, sens: -1 | 1): void {
    const depuis = rows.findIndex((row) => row.key === key);
    if (depuis < 0) return;
    const vers = depuis + sens;
    if (vers < 0 || vers > rows.length - 1) return;

    persisterLOrdre(deplacerLigne(rows, depuis, vers));
    // Sans cette annonce, le clavier et le lecteur d'écran déplacent à
    // l'aveugle : rien ne bouge sous le doigt, et le focus reste sur un bouton
    // qui a changé de sens (§4, AA en toutes conditions).
    setAnnonce(`Étape ${String(depuis + 1)} déplacée en position ${String(vers + 1)} sur ${String(rows.length)}.`);
  }

  /**
   * Monter ou descendre **un bloc entier**. Déplacer l'intitulé seul laisserait
   * les étapes de la phase sous la phase précédente : une façon silencieuse de
   * changer une recette.
   */
  function deplacerLeBloc(key: string, sens: -1 | 1): void {
    const index = rows.findIndex((row) => row.key === key);
    if (index < 0) return;
    const sections = sectionsDe(rows);
    const rang = sectionDe(rows, index);
    if (rang < 0 || rang + sens < 0 || rang + sens > sections.length - 1) return;

    const suivantes = deplacerSection(rows, index, sens);
    persisterLOrdre(suivantes);
    const label = sections[rang]?.label ?? '';
    setAnnonce(
      `Phase ${label} déplacée en position ${String(rang + sens + 1)} sur ${String(sections.length)}.`,
    );
  }


  /**
   * Conversion étape ↔ titre de phase (US-41). Même geste que côté ingrédients,
   * et même raison : une recette à deux préparations écrit souvent « Pour la
   * garniture » comme une étape à part entière, faute d'un endroit où la mettre.
   */
  function enTitre(key: string): void {
    const index = rows.findIndex((row) => row.key === key);
    if (index < 0) return;
    const next = transformerEnTitre(rows, index, (row) => row.instruction);
    setRows(next);
    onSave(toStepInputs(next));
  }

  function enEtape(key: string): void {
    const index = rows.findIndex((row) => row.key === key);
    if (index < 0) return;
    const next = transformerTitreEnLigne(rows, index, (texte, groupe) => {
      const cle = crypto.randomUUID();
      const etape: StepRow = { key: cle, id: null, group_label: groupe, instruction: texte, duration_sec: null, duree_texte: '', temperature_c: null, duration_source: null, temperature_source: null, is_passive: 0 };
      return etape;
    });
    setRows(next);
    onSave(toStepInputs(next));
  }

  /**
   * Arrivée depuis « Écrire les étapes » (US-74) : la section vient au premier
   * plan et prend le focus.
   *
   * Le premier champ de saisie s'il y en a un, sinon « Ajouter une étape » —
   * jamais une ligne créée d'office : arriver sur un écran ne doit rien écrire,
   * et une étape vide en base serait une écriture que personne n'a demandée.
   *
   * **Les champs d'abord, les boutons ensuite** (US-77). Le sélecteur unique
   * `'textarea, input, button'` rend le premier élément **dans l'ordre du
   * document**, et les commandes de déplacement précèdent désormais le texte de
   * l'étape : on serait arrivé sur « Monter l'étape 1 », désactivée de surcroît,
   * donc sur rien du tout. Deux recherches successives disent l'intention plutôt
   * que de dépendre de l'ordre du balisage — et le bouton n'est atteint que
   * lorsqu'il n'y a aucune étape, cas où il n'y a pas non plus de commande de
   * déplacement.
   *
   * `scrollIntoView` n'existe pas dans jsdom : la garde n'est pas de la
   * prudence décorative, elle évite de faire tomber tout l'écran en test.
   */
  const section = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!ouvrirSurLesEtapes) return;
    const cible = section.current?.querySelector<HTMLElement>('textarea, input')
      ?? section.current?.querySelector<HTMLElement>('button');
    if (cible === null || cible === undefined) return;
    if (typeof section.current?.scrollIntoView === 'function') {
      section.current.scrollIntoView({ block: 'start' });
    }
    cible.focus();
    // Une seule fois, à l'arrivée : reprendre le focus à chaque rendu
    // l'arracherait au champ dans lequel on est en train d'écrire.
  }, [ouvrirSurLesEtapes]);

  // Index global (avant regroupement) : les libellés « Étape N » et la
  // numérotation restent cohérents même quand un groupe démarre après le
  // début de la liste.
  const indexedRows = rows.map((row, index) => ({ ...row, stepNumber: index + 1 }));
  const groups = groupByLabel(indexedRows);

  return (
    <section ref={section} aria-label="Étapes" className="flex flex-col gap-3">
      <h2 className="text-heading-2">Étapes</h2>
      {/* Le déplacement ne se voit qu'à l'écran : sans cette région, le clavier
          et le lecteur d'écran réordonnent à l'aveugle (US-77, §4). */}
      <p role="status" aria-live="polite" data-testid="annonce-ordre-etapes" className="sr-only">{annonce}</p>
      <div data-testid="step-editor" className="flex flex-col gap-4">
        {groups.map((group) => {
          const premiere = group.items[0];
          const rangDeSection = premiere === undefined ? -1 : sectionDe(rows, premiere.stepNumber - 1);
          return (
          <div key={group.label ?? ' sans-groupe'} className="flex flex-col gap-2">
            {/* Même traitement que sur la fiche en lecture : un titre de phase
                a le poids d'un titre de section, prolongé d'un filet qui
                marque la rupture. Il ne doit pas pouvoir se confondre avec une
                ligne de la liste (relecture de CookBook, 10 août 2026). */}
            {group.label !== null && (
              <h3 className="flex items-center gap-3">
                <span className="text-heading-2">{group.label}</span>
                {/* Le bloc se déplace d'un seul geste (US-77) : une phase n'est
                    pas une ligne, c'est l'étiquette d'étapes consécutives. */}
                {premiere !== undefined && (
                  <span className="flex items-center">
                    <Button
                      type="button"
                      variant="quiet"
                      size="icon"
                      aria-label={`Monter la phase ${group.label}`}
                      disabled={rangDeSection <= 0}
                      onClick={() => { deplacerLeBloc(premiere.key, -1); }}
                    >
                      <Icon name="fleche-haut" />
                    </Button>
                    <Button
                      type="button"
                      variant="quiet"
                      size="icon"
                      aria-label={`Descendre la phase ${group.label}`}
                      disabled={rangDeSection < 0 || rangDeSection >= sectionsDe(rows).length - 1}
                      onClick={() => { deplacerLeBloc(premiere.key, 1); }}
                    >
                      <Icon name="fleche-bas" />
                    </Button>
                  </span>
                )}
                <span aria-hidden="true" className="h-px flex-1 bg-border" />
              </h3>
            )}
            <ol
              start={group.items[0]?.stepNumber ?? 1}
              className="flex list-decimal flex-col gap-2 pl-5 marker:text-foreground-muted"
            >
              {group.items.map((row) => {
                const stepId = row.id ?? null;
                // Durée et température sont désormais écrites à
                // l'enregistrement par la détection (US-78, D11) : plus de
                // proposition à appliquer d'un clic. Une valeur `'auto'` garde
                // le ton « à vérifier » (US-26, US-73) tant qu'une main ne l'a
                // pas touchée ; corrigée — ou vidée — elle devient une valeur
                // ordinaire, et la détection ne la reprend plus jamais.
                const dureeAVerifier = row.duration_source === 'auto' && row.duration_sec !== null;
                const tempAVerifier = row.temperature_source === 'auto' && row.temperature_c !== null;
                const rang = row.stepNumber - 1;
                return (
                <li
                  key={row.key}
                  className="flex flex-col gap-2 rounded-control border border-border p-2"
                >
                  {/* Déplacement (US-77). Deux commandes, et rien d'autre :
                      le glisser-déposer a été retiré faute de pouvoir être
                      vérifié sur l'appareil où il devait servir (`CLAUDE.md`,
                      ligne « L'ordre des étapes »). Désactivées aux extrémités
                      et jamais cachées : une commande qui disparaît déplace
                      celles d'à côté sous le doigt. */}
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="quiet"
                      size="icon"
                      aria-label={`Monter l’étape ${row.stepNumber}`}
                      disabled={rang === 0}
                      onClick={() => { deplacer(row.key, -1); }}
                    >
                      <Icon name="fleche-haut" />
                    </Button>
                    <Button
                      type="button"
                      variant="quiet"
                      size="icon"
                      aria-label={`Descendre l’étape ${row.stepNumber}`}
                      disabled={rang === rows.length - 1}
                      onClick={() => { deplacer(row.key, 1); }}
                    >
                      <Icon name="fleche-bas" />
                    </Button>
                  </div>
                  <textarea
                    aria-label={`Étape ${row.stepNumber}`}
                    className={inputClass}
                    rows={2}
                    value={row.instruction}
                    onChange={(e) => { updateRow(row.key, { instruction: e.target.value }); }}
                    onBlur={commitRows}
                  />
                  {/* Ingrédients de l'étape, en puces sous le texte (US-15).
                      Remplace la section « Association ingrédients ↔ étapes »,
                      une matrice de N × M cases à cocher natives de 13 px
                      présentée comme un écran à part entière — le marqueur
                      « outil interne » le plus visible de l'application
                      (audit externe, constat 03). Même donnée, même écriture,
                      mais à côté de l'étape qu'elle concerne. */}
                  {/* `stepId` non nul : une étape qui vient d'être ajoutée et
                      pas encore enregistrée n'a pas d'identifiant en base, donc
                      rien à associer. */}
                  {ingredients.length > 0 && stepId !== null && persistedStepIds.has(stepId) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-meta">Ingrédients utilisés :</span>
                      {ingredients.map((ingredient) => {
                        const on = linked.has(`${stepId} ${ingredient.id}`);
                        return (
                          <button
                            key={ingredient.id}
                            type="button"
                            aria-pressed={on}
                            aria-label={`Étape ${row.stepNumber} — ${ingredient.name}`}
                            onClick={() => { toggleIngredient(stepId, ingredient.id); }}
                            className={`min-h-touch rounded-full px-3 text-xs transition-colors ${
                              on
                                ? 'bg-primary text-primary-foreground'
                                : 'border border-border text-foreground-muted hover:border-foreground-muted'
                            }`}
                          >
                            {ingredient.name}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-xs text-foreground">
                    {/* Seule la durée reste visible : c'est elle qui alimente le
                        bouton minuteur du mode cuisine (§3.3). Température,
                        groupe et « étape passive » sont vides sur la totalité
                        des recettes observées et quadruplaient la hauteur de
                        chaque bloc — ils passent sous « Options ». */}
                    {/* **Le champ n'a plus d'unité, il en comprend** (US-98).
                        Il était `type="number"` et disait « Durée (min) » ;
                        la seconde le rend intenable — en minutes on ne peut
                        plus écrire « 20 sec », en secondes une cuisson de
                        trente minutes se tape 1800. Il accepte donc « 20 sec »,
                        « 3 min », « 1 h 30 », et un nombre nu vaut des minutes,
                        ce qu'il a toujours voulu dire.

                        La saisie n'est **lue qu'à la sortie du champ** : la
                        relire à chaque frappe ferait valoir « 1 h 3 » pendant
                        qu'on tape « 1 h 30 ». Le texte brut vit dans la ligne,
                        la valeur en base est posée au `blur`. */}
                    <label className="flex items-center gap-1">
                      Durée
                      <input
                        aria-label={`Durée de l’étape ${row.stepNumber}`}
                        aria-describedby={`duree-aide-${row.key}`}
                        className={`${inputClass} text-data w-24 ${dureeAVerifier ? 'border-warning bg-warning-bg' : ''}`}
                        type="text"
                        inputMode="text"
                        placeholder="20 sec"
                        value={row.duree_texte}
                        onChange={(e) => { updateRow(row.key, { duree_texte: e.target.value }); }}
                        onBlur={() => {
                          // Toute saisie — y compris vider — devient `'manuel'`
                          // (D11) : corriger une durée est un geste qu'on ne
                          // refait pas. La valeur relue reformate le champ, ce
                          // qui montre ce qui a été compris : « 90 » devient
                          // « 1 h 30 ».
                          const secondes = parseDureeSaisie(row.duree_texte);
                          updateRow(row.key, {
                            duration_sec: secondes,
                            duree_texte: formatDureeSecondes(secondes) ?? '',
                            duration_source: 'manuel',
                          });
                          commitRows();
                        }}
                      />
                    </label>
                    <span id={`duree-aide-${row.key}`} className="sr-only">
                      Minutes par défaut. Écrivez l’unité pour autre chose : 20 sec, 1 h 30.
                    </span>
                    {/* Une pastille seule ne dit pas ce qu'elle reproche (F-10) :
                        « à vérifier » posé sur un champ rempli se lit comme une
                        erreur alors que c'est une lecture automatique. La phrase
                        dit d'où vient la valeur, et `aria-describedby` la rend
                        atteignable au clavier — le champ l'annonce en prenant le
                        focus, sans avoir à survoler quoi que ce soit. */}
                    {dureeAVerifier && (
                      <span id={`duree-auto-${row.key}`} className="flex items-center gap-2">
                        <Badge tone="warning">à vérifier</Badge>
                        <span className="text-meta">détectée dans le texte de l’étape</span>
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      aria-expanded={openOptions.has(row.key)}
                      aria-label={`Options de l’étape ${row.stepNumber}`}
                      onClick={() => { toggleOptions(row.key); }}
                    >
                      {/* Le point signale une température détectée qui attend
                          un regard sous le panneau replié. */}
                      {tempAVerifier ? 'Options •' : 'Options'}
                    </Button>
                    {/* Bascule étape ↔ titre de phase (US-41). */}
                    {row.group_label !== null && ouvreUneSection(rows, rows.findIndex((r) => r.key === row.key)) ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="compact"
                        onClick={() => { enEtape(row.key); }}
                        aria-label={`Remettre le titre ${row.group_label} en étape`}
                      >
                        Remettre en étape
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="compact"
                        onClick={() => { enTitre(row.key); }}
                        disabled={row.instruction.trim().length === 0}
                        aria-label={`Faire de l’étape ${row.stepNumber} un titre de section`}
                      >
                        En faire un titre
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      className="ml-auto"
                      onClick={() => { removeRow(row.key); }}
                      aria-label={`Supprimer l’étape ${row.stepNumber}`}
                    >
                      Supprimer
                    </Button>
                  </div>

                  {openOptions.has(row.key) && (
                    <div className="flex flex-wrap items-center gap-3 border-t border-border pt-2 text-xs text-foreground">
                      <label className="flex items-center gap-1">
                        Température (°C)
                        <input
                          aria-label={`Température de l’étape ${row.stepNumber}`}
                          aria-describedby={tempAVerifier ? `temperature-auto-${row.key}` : undefined}
                          className={`${inputClass} w-16 ${tempAVerifier ? 'border-warning bg-warning-bg' : ''}`}
                          type="number"
                          inputMode="numeric"
                          value={row.temperature_c ?? ''}
                          onChange={(e) => {
                            updateRow(row.key, { temperature_c: toNullableNumber(e.target.value), temperature_source: 'manuel' });
                          }}
                          onBlur={commitRows}
                        />
                      </label>
                      {tempAVerifier && (
                        <span id={`temperature-auto-${row.key}`} className="flex items-center gap-2">
                          <Badge tone="warning">à vérifier</Badge>
                          <span className="text-meta">détectée dans le texte de l’étape</span>
                        </span>
                      )}
                      <label className="flex items-center gap-1">
                        Groupe
                        <input
                          aria-label={`Groupe de l’étape ${row.stepNumber}`}
                          className={`${inputClass} w-24`}
                          value={row.group_label ?? ''}
                          onChange={(e) => { updateRow(row.key, { group_label: e.target.value }); }}
                          onBlur={commitRows}
                        />
                      </label>
                      <span className="flex min-h-touch items-center gap-2">
                        <Checkbox
                          size="compact"
                          label="Le cuisinier n’est pas occupé pendant cette étape"
                          checked={row.is_passive === 1}
                          onChange={(next) => {
                            updateRow(row.key, { is_passive: next ? 1 : 0 });
                            commitRows();
                          }}
                        />
                        Le cuisinier n’est pas occupé pendant cette étape
                      </span>
                    </div>
                  )}
                </li>
                );
              })}
            </ol>
          </div>
          );
        })}
      </div>
      <Button type="button" variant="secondary" size="compact" className="w-fit" onClick={addRow}>
        + Ajouter une étape
      </Button>
    </section>
  );
}


/**
 * Écran d'édition d'une recette (`/recette/:id/modifier`, US-14).
 *
 * Jusqu'à US-14 ce composant faisait office de fiche : il servait à la fois la
 * consultation et la correction sur place (US-06). La consultation est
 * désormais un écran à part (`RecipeRead.tsx`), sans un seul champ de
 * formulaire, et celui-ci ne fait plus qu'éditer — d'où le retrait du bouton
 * « Cuisiner », qui appartient à la lecture, et le retour vers la fiche
 * plutôt que vers la bibliothèque. Son allègement (options avancées repliées,
 * fin de la matrice d'association) est le sujet d'US-15.
 */
export function RecipeDetail({ recipeId, onBack }: RecipeDetailProps): React.JSX.Element {
  /**
   * Section à mettre au premier plan (US-74). « Écrire les étapes », sur une
   * recette dont le procédé est resté dans la vidéo, ouvre cet écran : sans ce
   * paramètre on atterrit tout en haut d'une page de 2 000 px, et il reste à
   * trouver soi-même l'endroit qu'on venait de demander.
   *
   * Un paramètre de requête plutôt qu'une ancre `#edit-etapes` : le routeur ne
   * lit que le chemin et la requête (`readUrl`), et une ancre collée au chemin
   * empêcherait `matchRoute` de reconnaître l'écran.
   */
  const url = useUrl();
  const sectionDemandee = new URLSearchParams(url.split('?')[1] ?? '').get('section');
  // Suggestions de cuisine (US-55), filtrées par le serveur sur le périmètre
  // visible. Jamais bloquantes : un échec laisse la liste vide et le champ
  // reste libre. Le `Promise.resolve()` transforme une exception synchrone en
  // rejet — sans lui, un `fetch` indisponible ferait tomber tout l'écran.
  const [cuisinesConnues, setCuisinesConnues] = useState<string[]>([]);
  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(() => listCuisines())
      .then((noms) => { if (vivant) setCuisinesConnues(noms); })
      .catch(() => { /* liste vide */ });
    return () => { vivant = false; };
  }, []);

  const [detail, setDetail] = useState<RecipeDetailData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Hors ligne (US-12) : la modification a été mise en file, pas perdue —
  // affichée distinctement de `saveError`, qui reste réservé à un vrai échec.
  const [saveOffline, setSaveOffline] = useState(false);
  // Ajout d'une réalisation a posteriori (US-10 : « reste ajoutable a
  // posteriori depuis la fiche recette »), indépendant du mode cuisine —
  // même formulaire (`CookLogForm`) que l'écran de fin, sans préremplissage
  // d'`actual_minutes` (pas de session de cuisine en cours).
  const [addingCookLog, setAddingCookLog] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  // Archivage (refonte UX/UI passe 4) : action en cours, pour désactiver le
  // bouton le temps de l'aller-retour réseau plutôt que de laisser un double
  // clic déclencher deux écritures.
  const [archiving, setArchiving] = useState(false);

  // Recette changée : on efface l'ancienne avant de charger la nouvelle.
  // Ajustement pendant le rendu (pas dans l'effet ci-dessous, qui ne doit
  // appeler setState que depuis ses callbacks asynchrones).
  const [syncedRecipeId, setSyncedRecipeId] = useState(recipeId);
  if (recipeId !== syncedRecipeId) {
    setSyncedRecipeId(recipeId);
    setDetail(null);
    setLoadError(null);
    setSaveOffline(false);
    setAddingCookLog(false);
  }

  useEffect(() => {
    const controller = new AbortController();
    getRecipe(recipeId, controller.signal)
      .then(setDetail)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setLoadError(e.message);
      });
    return () => { controller.abort(); };
  }, [recipeId]);

  /** Commun aux quatre écritures ci-dessous : file hors ligne (US-12) distinguée d'un vrai échec. */
  const handleSaveError = useCallback((e: unknown) => {
    if (e instanceof OfflineQueuedError) {
      setSaveOffline(true);
      setSaveError(null);
      return;
    }
    setSaveOffline(false);
    setSaveError(e instanceof Error ? e.message : 'Erreur inconnue.');
  }, []);

  // Renvoie un booléen de succès (jamais un rejet) : `EditableField` (B5,
  // refonte UX/UI passe 4) s'en sert pour afficher son propre indicateur
  // « Enregistré » sans dupliquer la gestion d'erreur, déjà faite ici par
  // `handleSaveError` (bandeau global `saveError`/`saveOffline`).
  const saveRecipeField = useCallback(
    (patch: Parameters<typeof updateRecipe>[1]): Promise<boolean> =>
      updateRecipe(recipeId, patch)
        .then((updated) => { setDetail(updated); setSaveError(null); setSaveOffline(false); return true; })
        .catch((e: unknown) => { handleSaveError(e); return false; }),
    [recipeId, handleSaveError],
  );

  const saveIngredients = useCallback(
    (rows: IngredientInput[]) => {
      updateIngredients(recipeId, rows)
        .then((updated) => { setDetail(updated); setSaveError(null); setSaveOffline(false); })
        .catch(handleSaveError);
    },
    [recipeId, handleSaveError],
  );

  const saveSteps = useCallback(
    (rows: StepInput[]) => {
      updateSteps(recipeId, rows)
        .then((updated) => { setDetail(updated); setSaveError(null); setSaveOffline(false); })
        .catch(handleSaveError);
    },
    [recipeId, handleSaveError],
  );

  /**
   * L'ordre des étapes (US-77) — sa propre écriture, et pas `saveSteps`.
   *
   * `PUT .../steps` efface puis réinsère toutes les étapes, ce qui fait
   * recalculer l'appariement étape ↔ ingrédient et perd les corrections faites
   * à la main (US-08). Un déplacement ne change rien du contenu : il n'écrit
   * que `position` et `group_label`.
   */
  const saveStepOrder = useCallback(
    (steps: StepOrderInput[]) => {
      reorderSteps(recipeId, steps)
        .then((updated) => { setDetail(updated); setSaveError(null); setSaveOffline(false); })
        .catch(handleSaveError);
    },
    [recipeId, handleSaveError],
  );

  const saveStepIngredients = useCallback(
    (pairs: StepIngredientInput[]) => {
      updateStepIngredients(recipeId, pairs)
        .then((updated) => { setDetail(updated); setSaveError(null); setSaveOffline(false); })
        .catch(handleSaveError);
    },
    [recipeId, handleSaveError],
  );

  /**
   * Bascule archiver/restaurer (refonte UX/UI passe 4) : une seule action,
   * son sens dépend de l'état courant de la recette — même geste qu'un
   * commutateur, pas deux boutons distincts qui pourraient tous les deux
   * rester affichés. Pas de confirmation bloquante (`window.confirm`,
   * jamais utilisé ailleurs dans l'app) : le geste reste réversible en un
   * clic depuis la fiche elle-même (§3 CLAUDE.md « on archive, on ne
   * supprime jamais »).
   */
  const handleArchiveToggle = useCallback(() => {
    const alreadyArchived = detail !== null && detail.archived === 1;
    setArchiving(true);
    (alreadyArchived ? restoreRecipe(recipeId) : archiveRecipe(recipeId))
      .then((updated) => { setDetail(updated); setSaveError(null); setSaveOffline(false); })
      .catch(handleSaveError)
      .finally(() => { setArchiving(false); });
  }, [recipeId, detail, handleSaveError]);

  function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoError(null);
    setPhotoUploading(true);
    uploadPhoto(recipeId, file)
      .then(setDetail)
      .catch((e: unknown) => { setPhotoError(e instanceof Error ? e.message : 'Erreur inconnue.'); })
      .finally(() => { setPhotoUploading(false); });
  }

  if (loadError !== null) {
    return (
      <div className="flex flex-col gap-4">
        <Button type="button" variant="ghost" size="inline" className="self-start" onClick={onBack}><Icon name="fleche-gauche" size={0.9} /> Retour à la recette</Button>
        <Banner tone="danger">Impossible de charger la recette : {loadError}</Banner>
      </div>
    );
  }

  if (detail === null) {
    return <Loading label="la recette" />;
  }

  return (
    <div className="flex flex-col gap-6" aria-label={`Fiche recette : ${detail.title}`}>
      {/*
        **L'écran d'édition n'avait pas de titre** (refonte éditoriale) — ni
        `h1`, ni `h2` de premier rang : on arrivait sur un lien de retour puis
        un champ « Titre », et le seul repère de l'endroit où l'on se trouvait
        était le contenu de ce champ. Le §4 de CLAUDE.md demande un plan de
        titres par écran ; c'était le dernier des onze à n'en avoir aucun.

        Le nom de la recette passe en repère plutôt qu'en titre : le titre de
        cet écran-ci, c'est ce qu'on y fait. Le nom, lui, est juste en dessous,
        modifiable — l'afficher deux fois, dont une non modifiable, poserait
        la question de savoir laquelle des deux fait foi.
      */}
      <PageHeader
        eyebrow={detail.title}
        title="Modifier la recette"
        back={<Button type="button" variant="quiet" size="compact" onClick={onBack}><Icon name="fleche-gauche" size={0.9} /> Retour à la recette</Button>}
      />

      {saveError !== null && <Banner tone="danger">{saveError}</Banner>}
      {saveOffline && (
        <Banner tone="warning">
          Hors connexion : la modification a été mise en file, elle sera enregistrée à la reconnexion.
        </Banner>
      )}
      {/* Archivage (refonte UX/UI passe 4) : visible dès l'ouverture de la
          fiche, pour qu'une recette atteinte par un autre chemin que la
          bibliothèque (ex. historique du navigateur) ne laisse jamais croire
          qu'elle y est toujours listée. */}
      {detail.archived === 1 && (
        <Banner tone="warning">Cette recette est archivée : elle n’apparaît plus dans la bibliothèque ni dans la recherche.</Banner>
      )}

      {/*
        Sommaire latéral, même mécanisme que les réglages : **des ancres, pas
        des onglets**. Tout reste monté, la recherche du navigateur trouve
        tout, rien ne se cache derrière un clic. Sur un écran qui fait
        2 000 px de haut dès qu'une recette porte quinze ingrédients et vingt
        étapes, la seule façon d'atteindre les étiquettes était de faire
        défiler devant tout le reste.
      */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[12rem_minmax(0,1fr)] lg:gap-10">
        <nav
          aria-label="Sections de la fiche"
          className="-mx-6 overflow-x-auto px-6 lg:mx-0 lg:sticky lg:top-8 lg:self-start lg:overflow-visible lg:px-0"
        >
          <ul className="flex gap-1 lg:flex-col">
            {[
              ['#edit-informations', 'Informations'],
              ['#edit-ingredients', 'Ingrédients'],
              ['#edit-etapes', 'Étapes'],
              ['#edit-etiquettes', 'Étiquettes'],
              ['#edit-journal', 'Journal'],
            ].map(([href, label]) => (
              <li key={href}>
                <a
                  href={href}
                  className={`flex min-h-touch-min items-center whitespace-nowrap rounded-control px-3 text-sm font-medium text-foreground-secondary no-underline transition duration-(--duration-quick) ease-entrance hover:bg-surface-sunken hover:text-foreground ${FOCUS_RING}`}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex min-w-0 flex-col gap-8">
      {/* Photo et titre, et
          dernière note du journal juste en dessous : tout ce qui doit être
          visible sans défilement (US-06) tient dans cet en-tête. */}
      <div id="edit-informations" className="flex scroll-mt-8 flex-col gap-4 sm:flex-row sm:items-start">
        <PhotoField
          imagePath={detail.image_path}
          uploading={photoUploading}
          error={photoError}
          onChange={handlePhotoChange}
        />

        <div className="flex flex-1 flex-col gap-3">
          <label className={labelClass}>
            Titre
            <EditableField
              value={detail.title}
              onCommit={(raw) => saveRecipeField({ title: raw })}
              required
              className="text-heading-1"
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            {/* Action primaire de la fiche (§10 mission) : seul bouton plein,
                volontairement plus affirmé que ses voisins ci-dessous. */}
            {/* Ajout a posteriori (US-10, AC : « reste ajoutable a posteriori
                depuis la fiche recette ») — hors du mode cuisine, pas de durée
                mesurée à préremplir. */}
            <Button type="button" variant="secondary" className="w-fit" onClick={() => { setAddingCookLog(true); }}>
              Ajouter une réalisation
            </Button>
            {/* Export PDF d'une recette seule (US-12, AC : « lisible et
                imprimable ») : un lien direct, même principe que l'export
                complet (DataBackup.tsx) — le serveur répond en
                `content-disposition: attachment`. */}
            <LinkButton href={recipePdfUrl(recipeId)} download className="w-fit">
              Exporter en PDF
            </LinkButton>
            {/* Archiver/restaurer (refonte UX/UI passe 4) : `ghost`, la moins
                affirmée des trois variantes — action peu fréquente, jamais
                destructrice (§3 CLAUDE.md), elle ne doit pas rivaliser avec
                « Cuisiner ». */}
            <Button type="button" variant="ghost" className="w-fit" onClick={handleArchiveToggle} disabled={archiving}>
              {detail.archived === 1 ? 'Restaurer' : 'Archiver'}
            </Button>
          </div>

          <LastCookLog latestCookLog={detail.latest_cook_log} />

          {addingCookLog && (
            <div className={cardClasses()}>
              <CookLogForm
                recipeId={recipeId}
                prefilledActualMinutes={null}
                dismissLabel="Annuler"
                onSaved={(updated) => { setDetail(updated); setAddingCookLog(false); }}
                onDismiss={() => { setAddingCookLog(false); }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <label className={labelClass}>
          Portions
          <EditableField
            value={detail.servings !== null ? String(detail.servings) : ''}
            onCommit={(raw) => saveRecipeField({ servings: toNullableNumber(raw) })}
            type="number"
            className="text-data"
          />
        </label>
        <label className={labelClass}>
          Unité de portions
          <EditableField
            value={detail.servings_label ?? ''}
            onCommit={(raw) => saveRecipeField({ servings_label: toNullableText(raw) })}
            placeholder="personnes"
          />
        </label>
        {/* Nature du plat et cuisine (US-55). Sur **l'écran d'édition**, et pas
            seulement sur le formulaire de création : sans cela, les recettes
            déjà en bibliothèque — la totalité, le jour de la livraison —
            n'auraient aucun moyen d'en recevoir une. */}
        <label className={labelClass}>
          Nature du plat
          <Select
            aria-label="Nature du plat"
            value={detail.course ?? ''}
            onChange={(e) => {
              const valeur = e.target.value === '' ? null : (e.target.value as RecipeCourse);
              void saveRecipeField({ course: valeur });
            }}
          >
            <option value="">—</option>
            {RECIPE_COURSES.map((valeur) => (
              <option key={valeur} value={valeur}>{formatCourse(valeur)}</option>
            ))}
          </Select>
        </label>
        <label className={labelClass}>
          Cuisine
          <EditableField
            value={detail.cuisine ?? ''}
            onCommit={(raw) => saveRecipeField({ cuisine: toNullableText(raw) })}
            placeholder="italienne"
            list="mlc-cuisines-edition"
          />
          {/* Suggestions filtrées par le serveur : une cuisine qui n'existerait
              que dans une recette privée d'un autre en prouverait l'existence
              (US-46, vecteur 4). */}
          <datalist id="mlc-cuisines-edition">
            {cuisinesConnues.map((nom) => <option key={nom} value={nom} />)}
          </datalist>
        </label>
        <label className={labelClass}>
          Difficulté
          <Select
            aria-label="Difficulté"
            value={detail.difficulty ?? ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : (Number(e.target.value) as Difficulty);
              void saveRecipeField({ difficulty: value });
            }}
          >
            <option value="">—</option>
            <option value="1">Facile</option>
            <option value="2">Moyen</option>
            <option value="3">Difficile</option>
          </Select>
        </label>
        {/* « Je l'avais déjà faite avant » (US-61). La colonne `cooked_before`
            existe depuis la migration `008` et n'était écrite que par le script
            d'import : l'étiquette « jamais cuisinée » ne pouvait donc être
            corrigée depuis aucun écran, sur aucune des vingt-neuf recettes
            reprises de CookBook. */}
        <div className="flex items-center gap-3">
          <Checkbox
            label="Je l’avais déjà faite avant d’ajouter cette recette"
            checked={detail.cooked_before === 1}
            onChange={() => { void saveRecipeField({ cooked_before: detail.cooked_before !== 1 }); }}
          />
          <span className="text-sm text-foreground-secondary">
            Je l’avais déjà faite avant d’ajouter cette recette
          </span>
        </div>
        <label className={labelClass}>
          Source
          <EditableField
            value={detail.source_name ?? ''}
            onCommit={(raw) => saveRecipeField({ source_name: toNullableText(raw) })}
          />
        </label>
        {/* Champ distinct depuis US-27 : « Source » recevait le pseudonyme de
            l'auteur, et le site n'apparaissait nulle part. */}
        <label className={labelClass}>
          Auteur
          <EditableField
            value={detail.author ?? ''}
            onCommit={(raw) => saveRecipeField({ author: toNullableText(raw) })}
          />
        </label>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <label className={labelClass}>
          Préparation (min)
          <EditableField
            value={detail.prep_min !== null ? String(detail.prep_min) : ''}
            onCommit={(raw) => saveRecipeField({ prep_min: toNullableNumber(raw) })}
            type="number"
            className="text-data"
          />
        </label>
        <label className={labelClass}>
          Repos (min)
          <EditableField
            value={detail.rest_min !== null ? String(detail.rest_min) : ''}
            onCommit={(raw) => saveRecipeField({ rest_min: toNullableNumber(raw) })}
            type="number"
            className="text-data"
          />
        </label>
        <label className={labelClass}>
          Cuisson (min)
          <EditableField
            value={detail.cook_min !== null ? String(detail.cook_min) : ''}
            onCommit={(raw) => saveRecipeField({ cook_min: toNullableNumber(raw) })}
            type="number"
            className="text-data"
          />
        </label>
      </div>

      {/* Étiquettes manuelles (US-21) : le rangement que l'utilisateur fait
          lui-même, à côté des quatre étiquettes calculées d'US-05. */}
      <div id="edit-etiquettes" className="scroll-mt-8" />
      <TagsEditor
        tags={detail.tags}
        onChange={(names) =>
          updateRecipeTags(recipeId, names)
            .then((applied) => {
              // Réponse = étiquettes manuelles seules : les calculées de la
              // fiche sont conservées telles quelles, la route n'y touche pas.
              setDetail((current) =>
                current === null
                  ? current
                  : { ...current, tags: [...current.tags.filter((tag) => tag.kind === 'auto'), ...applied] },
              );
            })
            .catch((e: unknown) => { setSaveError(e instanceof Error ? e.message : String(e)); })
        }
      />

      <label className={labelClass}>
        Description
        <EditableField
          value={detail.description ?? ''}
          onCommit={(raw) => saveRecipeField({ description: toNullableText(raw) })}
          multiline
        />
      </label>

      <ServingsAdapter recipeId={recipeId} referenceServings={detail.servings} ingredients={detail.ingredients} />

      <div id="edit-ingredients" className="scroll-mt-8" />
      <IngredientsEditor ingredients={detail.ingredients} onSave={saveIngredients} />
      <div id="edit-etapes" className="scroll-mt-8" />
      <StepsEditor
        steps={detail.steps}
        ingredients={detail.ingredients}
        stepIngredients={detail.step_ingredients}
        onSave={saveSteps}
        onReorder={saveStepOrder}
        onSaveStepIngredients={saveStepIngredients}
        ouvrirSurLesEtapes={sectionDemandee === 'etapes'}
      />

      {/* **La note a quitté cet écran** (US-64). Elle est personnelle : elle
          s'écrit depuis la fiche en lecture, ouverte à quiconque voit la
          recette, alors que cet écran-ci est réservé à son propriétaire. La
          laisser ici aurait interdit à Claire d'annoter la Tropézienne
          d'Arthur — exactement ce que le rangement avait rencontré en US-51 —
          et le champ aurait de surcroît écrit dans le vide, `notes` ayant
          quitté le corps de `PATCH /api/recipes/:id`. */}

      <div id="edit-journal" className="scroll-mt-8" />
      <CookLogHistory cookLogs={detail.cook_logs} />

      {/* Surcouche plein écran (US-08) : pas de nouvel écran dans App.tsx, le
          mode cuisine recouvre simplement la fiche pendant qu'il est actif.
          `onCookLogSaved` (US-10) rafraîchit la fiche (dernière note,
          historique) dès que l'écran de fin du mode cuisine enregistre une
          réalisation, sans attendre un rechargement. */}
      {/* Le mode cuisine a quitté cet écran (US-58) : `App.tsx` passe
          `cooking={false}` en dur à l'écran d'édition depuis US-13, ce bloc
          n'avait donc plus aucun chemin d'exécution. */}
        </div>
      </div>
    </div>
  );
}
